import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Search, Users, Building2, GraduationCap, FileText, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RechercheUnifiee() {
  const { getAuthHeader } = useAuth();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleSearch = async () => {
    if (!query || query.length < 2) {
      toast.error('Veuillez saisir au moins 2 caractères');
      return;
    }

    setLoading(true);
    try {
      const headers = getAuthHeader();
      const response = await axios.get(
        `${API}/admin/recherche-unifiee?query=${encodeURIComponent(query)}`,
        { headers }
      );
      setResults(response.data.resultats);
    } catch {
      toast.error('Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const totalResults = results
    ? (results.membres?.length || 0) +
      (results.coopers?.length || 0) +
      (results.experts?.length || 0) +
      (results.souscriptions?.length || 0) +
      (results.dommages?.length || 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Hero Header — Ultra Pro */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-900 to-violet-900 p-6 lg:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(139,92,246,0.4),_transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(99,102,241,0.25),_transparent_45%)] pointer-events-none" />
        <div className="relative">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-purple-400 flex items-center justify-center shadow-lg shadow-violet-500/40 flex-shrink-0">
              <Search className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-200 border border-violet-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
                  Recherche cross-entités
                </span>
                {results && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 backdrop-blur-sm font-semibold text-xs">
                    {totalResults} résultat{totalResults > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold">Recherche Unifiée</h2>
              <p className="text-indigo-100 mt-1 text-sm lg:text-base">
                Membres, COOPER&apos;S, Experts, Souscriptions, Dommages — par Nom, Entreprise, N°Adhérent, Réf. Solidaire, Réf. Expert, Réf. ESS.
              </p>
            </div>
          </div>

          {/* Barre de recherche intégrée */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-violet-200" />
              <Input
                type="text"
                placeholder="Ex: Jean Dupont, ADH-025-00001, EXP-025-00001..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pl-12 py-6 text-base bg-white/10 border-white/20 text-white placeholder:text-violet-200 backdrop-blur-sm focus:bg-white/15"
                data-testid="recherche-unifiee-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={loading}
              className="px-8 py-6 text-base bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white border-0 shadow-lg shadow-cyan-500/30"
              data-testid="recherche-unifiee-submit"
            >
              {loading ? (
                <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Recherche...</>
              ) : (
                <><Search className="h-5 w-5 mr-2" /> Rechercher</>
              )}
            </Button>
          </div>

          {/* Suggestions rapides */}
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs uppercase tracking-wider text-violet-200 self-center mr-1 font-semibold">Préfixes :</span>
            {[
              { prefix: 'ADH-025-', label: 'Membres', color: 'bg-blue-500/20 text-blue-200 border-blue-400/30' },
              { prefix: 'COOP-025-', label: "COOPER'S", color: 'bg-purple-500/20 text-purple-200 border-purple-400/30' },
              { prefix: 'EXP-025-', label: 'Experts', color: 'bg-orange-500/20 text-orange-200 border-orange-400/30' },
              { prefix: 'SOL-025-', label: 'Solidaire', color: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30' },
            ].map(s => (
              <button
                key={s.prefix}
                onClick={() => setQuery(s.prefix)}
                className={`px-3 py-1 rounded-full text-xs font-mono font-semibold border backdrop-blur-sm transition-all hover:scale-105 ${s.color}`}
                data-testid={`recherche-suggestion-${s.prefix}`}
              >
                {s.prefix}
                <span className="ml-1.5 opacity-75 font-sans">{s.label}</span>
              </button>
            ))}
          </div>

          {/* Mini résumé après recherche */}
          {results && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5">
              {[
                { label: 'Membres', count: results.membres?.length || 0, icon: Users, color: 'text-blue-300' },
                { label: "COOPER'S", count: results.coopers?.length || 0, icon: Building2, color: 'text-purple-300' },
                { label: 'Experts', count: results.experts?.length || 0, icon: GraduationCap, color: 'text-orange-300' },
                { label: 'Souscriptions', count: results.souscriptions?.length || 0, icon: FileText, color: 'text-cyan-300' },
                { label: 'Dommages', count: results.dommages?.length || 0, icon: AlertCircle, color: 'text-rose-300' },
              ].map(c => (
                <div key={c.label} className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-3">
                  <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider ${c.color}`}>
                    <c.icon className="h-3.5 w-3.5" /> {c.label}
                  </div>
                  <p className="text-2xl font-bold mt-0.5">{c.count}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Résultats */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Résultats de recherche</span>
              <Badge className="bg-blue-700 text-white text-lg px-3 py-1 font-semibold">
                {results.membres?.length + results.coopers?.length + results.experts?.length + 
                 results.souscriptions?.length + results.dommages?.length || 0} résultat(s)
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="all">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                <TabsTrigger value="all">
                  Tout ({results.membres?.length + results.coopers?.length + results.experts?.length + 
                         results.souscriptions?.length + results.dommages?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="membres">
                  <Users className="h-4 w-4 mr-1" />
                  Membres ({results.membres?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="coopers">
                  <Building2 className="h-4 w-4 mr-1" />
                  COOPER'S ({results.coopers?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="experts">
                  <GraduationCap className="h-4 w-4 mr-1" />
                  Experts ({results.experts?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="souscriptions">
                  <FileText className="h-4 w-4 mr-1" />
                  Souscriptions ({results.souscriptions?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="dommages">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Dommages ({results.dommages?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-4">
                <div className="space-y-4">
                  {renderAllResults()}
                </div>
              </TabsContent>

              <TabsContent value="membres" className="mt-4">
                {renderMembres()}
              </TabsContent>

              <TabsContent value="coopers" className="mt-4">
                {renderCoopers()}
              </TabsContent>

              <TabsContent value="experts" className="mt-4">
                {renderExperts()}
              </TabsContent>

              <TabsContent value="souscriptions" className="mt-4">
                {renderSouscriptions()}
              </TabsContent>

              <TabsContent value="dommages" className="mt-4">
                {renderDommages()}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {results && !results.membres?.length && !results.coopers?.length && 
       !results.experts?.length && !results.souscriptions?.length && !results.dommages?.length && (
        <Card>
          <CardContent className="pt-12 pb-12 text-center">
            <Search className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <p className="text-lg text-slate-600">Aucun résultat trouvé pour "{query}"</p>
            <p className="text-sm text-slate-500 mt-2">
              Essayez avec un autre terme de recherche
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  function renderAllResults() {
    const all = [
      ...(results.membres || []).map(m => ({ ...m, type: 'membre' })),
      ...(results.coopers || []).map(c => ({ ...c, type: 'cooper' })),
      ...(results.experts || []).map(e => ({ ...e, type: 'expert' })),
      ...(results.souscriptions || []).map(s => ({ ...s, type: 'souscription' })),
      ...(results.dommages || []).map(d => ({ ...d, type: 'dommage' }))
    ];

    if (all.length === 0) return null;

    return all.map((item, idx) => (
      <Card key={idx} className="hover:shadow-lg transition-shadow">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTypeIcon(item.type)}
              <div>
                <p className="font-semibold">{getItemName(item)}</p>
                <p className="text-sm text-slate-600">{getItemDetails(item)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getTypeBadge(item.type)}
              <Button size="sm" variant="outline">
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    ));
  }

  function renderMembres() {
    if (!results.membres || results.membres.length === 0) {
      return <p className="text-center text-slate-600 py-4 md:py-8">Aucun membre trouvé</p>;
    }

    return (
      <div className="space-y-3">
        {results.membres.map((membre, idx) => (
          <Card key={idx} className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">
                    {membre.prenom} {membre.nom}
                  </p>
                  <p className="text-sm text-slate-600">{membre.email}</p>
                  {membre.numero_adherent && (
                    <Badge className="mt-2 bg-blue-700 text-white font-semibold">
                      N° Adhérent: {membre.numero_adherent}
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Voir le profil
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  function renderCoopers() {
    if (!results.coopers || results.coopers.length === 0) {
      return <p className="text-center text-slate-600 py-4 md:py-8">Aucun COOPER'S trouvé</p>;
    }

    return (
      <div className="space-y-3">
        {results.coopers.map((cooper, idx) => (
          <Card key={idx} className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{cooper.entreprise || cooper.raison_sociale}</p>
                  <p className="text-sm text-slate-600">{cooper.user?.email}</p>
                  {cooper.ref_ess && (
                    <Badge className="mt-2 bg-green-700 text-white font-semibold">
                      Réf. ESS: {cooper.ref_ess}
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Voir le profil
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  function renderExperts() {
    if (!results.experts || results.experts.length === 0) {
      return <p className="text-center text-slate-600 py-4 md:py-8">Aucun expert trouvé</p>;
    }

    return (
      <div className="space-y-3">
        {results.experts.map((expert, idx) => (
          <Card key={idx} className="hover:shadow-lg transition-shadow border-l-4 border-l-purple-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">
                    {expert.prenom} {expert.nom}
                  </p>
                  <p className="text-sm text-slate-600">{expert.email}</p>
                  {expert.ref_expert && (
                    <Badge className="mt-2 bg-purple-700 text-white font-semibold">
                      Réf. Expert: {expert.ref_expert}
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Voir le profil
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  function renderSouscriptions() {
    if (!results.souscriptions || results.souscriptions.length === 0) {
      return <p className="text-center text-slate-600 py-4 md:py-8">Aucune souscription trouvée</p>;
    }

    return (
      <div className="space-y-3">
        {results.souscriptions.map((souscription, idx) => (
          <Card key={idx} className="hover:shadow-lg transition-shadow border-l-4 border-l-orange-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{souscription.raison_sociale}</p>
                  <p className="text-sm text-slate-600">
                    Réf.Solidaire: {souscription.numero_police || souscription.numero_souscription}
                  </p>
                  <div className="flex gap-2 mt-2">
                    {souscription.ref_solidaire && (
                      <Badge className="bg-orange-600 text-white font-semibold">
                        Réf. Solidaire: {souscription.ref_solidaire}
                      </Badge>
                    )}
                    {souscription.numero_adherent && (
                      <Badge className="bg-blue-700 text-white font-semibold">
                        N° Adhérent: {souscription.numero_adherent}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Voir détails
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  function renderDommages() {
    if (!results.dommages || results.dommages.length === 0) {
      return <p className="text-center text-slate-600 py-4 md:py-8">Aucun dommage trouvé</p>;
    }

    return (
      <div className="space-y-3">
        {results.dommages.map((dommage, idx) => (
          <Card key={idx} className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-lg">{dommage.raison_sociale}</p>
                  <p className="text-sm text-slate-600">
                    N° Dommage: {dommage.numero_dommage}
                  </p>
                  {dommage.ref_expert && (
                    <Badge className="mt-2 bg-red-700 text-white font-semibold">
                      Réf. Expert: {dommage.ref_expert}
                    </Badge>
                  )}
                </div>
                <Button size="sm" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Voir dossier
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  function getTypeIcon(type) {
    const icons = {
      membre: <Users className="h-6 w-6 text-blue-600" />,
      cooper: <Building2 className="h-6 w-6 text-green-600" />,
      expert: <GraduationCap className="h-6 w-6 text-purple-600" />,
      souscription: <FileText className="h-6 w-6 text-orange-600" />,
      dommage: <AlertCircle className="h-6 w-6 text-red-600" />
    };
    return icons[type] || <Search className="h-6 w-6 text-slate-600" />;
  }

  function getTypeBadge(type) {
    const badges = {
      membre: <Badge className="bg-blue-700 text-white font-semibold">👤 Membre</Badge>,
      cooper: <Badge className="bg-green-700 text-white font-semibold">🏢 COOPER'S</Badge>,
      expert: <Badge className="bg-purple-700 text-white font-semibold">🎓 Expert</Badge>,
      souscription: <Badge className="bg-orange-600 text-white font-semibold">📄 Souscription</Badge>,
      dommage: <Badge className="bg-red-700 text-white font-semibold">⚠️ Dommage</Badge>
    };
    return badges[type] || <Badge className="bg-slate-700 text-white font-semibold">Autre</Badge>;
  }

  function getItemName(item) {
    if (item.type === 'membre' || item.type === 'expert') {
      return `${item.prenom} ${item.nom}`;
    }
    if (item.type === 'cooper') {
      return item.entreprise || item.raison_sociale;
    }
    return item.raison_sociale || item.numero_dommage || 'Sans nom';
  }

  function getItemDetails(item) {
    if (item.numero_adherent) return `N° Adhérent: ${item.numero_adherent}`;
    if (item.ref_ess) return `Réf. ESS: ${item.ref_ess}`;
    if (item.ref_expert) return `Réf. Expert: ${item.ref_expert}`;
    if (item.ref_solidaire) return `Réf. Solidaire: ${item.ref_solidaire}`;
    return item.email || '';
  }
}
